import type { DealScreeningFact } from "../../types/dealScreening";
import { stripMarks } from "./inlineMarks";
import { diffText } from "./memoDiff";

type Alternate = DealScreeningFact["alternates"][number];

export interface EnteredPart {
  /** Plain-text positions in the entered value (`stripMarks`). */
  from: number;
  to: number;
  /** The person's own words; the rest is the document's reading. */
  added: boolean;
}

/** How much of `base` survives in `edited`, 0..1, by characters of same words. */
const kept = (base: string, edited: string): number => {
  if (!base.trim()) return 0;
  const same = diffText(base, edited)
    .filter((part) => part.kind === "same")
    .reduce((n, part) => n + part.text.length, 0);
  return same / base.length;
};

/**
 * The document reading a person's entry was built on: the one of the
 * fact's other readings that the entered text keeps most of, when it keeps
 * at least half. A typed value that resembles no reading has no base, and is
 * all the person's own (DOP-658).
 */
export function baseReading(fact: DealScreeningFact): Alternate | null {
  if (fact.unit !== "text" || typeof fact.value !== "string") return null;
  const edited = stripMarks(fact.value);
  let best: Alternate | null = null;
  let score = 0;
  for (const alt of fact.alternates) {
    if (
      typeof alt.value !== "string" ||
      !alt.source ||
      alt.source.kind === "user"
    )
      continue;
    const s = kept(stripMarks(alt.value), edited);
    if (s > score) {
      score = s;
      best = alt;
    }
  }
  return score >= 0.5 ? best : null;
}

/**
 * The entered value split into the reading's words and the person's, by
 * plain positions. Words the reading had and the person removed are not
 * shown: the value is what stands now.
 */
export function enteredParts(
  fact: DealScreeningFact,
  base: Alternate,
): EnteredPart[] {
  const edited = stripMarks(String(fact.value));
  const parts: EnteredPart[] = [];
  let at = 0;
  for (const part of diffText(stripMarks(String(base.value)), edited)) {
    if (part.kind === "removed") continue;
    const to = at + part.text.length;
    const added = part.kind === "added";
    const last = parts[parts.length - 1];
    if (last && last.added === added) last.to = to;
    else parts.push({ from: at, to, added });
    at = to;
  }
  return parts;
}

export interface SpacedPart extends EnteredPart {
  /** The part began or ended on a space, which is drawn outside its box. */
  spaceBefore: boolean;
  spaceAfter: boolean;
}

/**
 * `enteredParts` with each part's edge spaces taken out of it: every part is
 * drawn as its own inline box, and a box does not show a space at its edge.
 * A part that is only spaces is one space and no box.
 */
export function spacedParts(
  fact: DealScreeningFact,
  base: Alternate,
): SpacedPart[] {
  const plain = stripMarks(String(fact.value));
  const out: SpacedPart[] = [];
  for (const part of enteredParts(fact, base)) {
    const text = plain.slice(part.from, part.to);
    const from = part.from + (text.length - text.trimStart().length);
    const to = part.to - (text.length - text.trimEnd().length);
    if (to <= from) {
      const last = out[out.length - 1];
      if (last) last.spaceAfter = true;
      continue;
    }
    out.push({
      ...part,
      from,
      to,
      spaceBefore: from > part.from,
      spaceAfter: to < part.to,
    });
  }
  return out;
}
